import React from 'react';
import { FDTreemapBotData } from './FDTreemapChart';

interface BotDetailModalProps {
  bot: FDTreemapBotData & { botId: string };
  currentHour: number;
  onClose: () => void;
}

/**
 * Bot Detail Modal - Full screen overlay with detailed bot analytics
 */
const BotDetailModal: React.FC<BotDetailModalProps> = ({ bot, currentHour, onClose }) => {
  const hourly = bot.hourly || { hoje: [], ontem: [], semana_anterior: [] };

  // Calculate max value for chart scaling
  const allValues = [...hourly.hoje, ...hourly.ontem, ...hourly.semana_anterior];
  const maxValue = Math.max(...allValues, 1);

  // Generate SVG path for a series
  const generatePath = (data: number[], chartWidth: number, chartHeight: number) => {
    if (!data || data.length === 0) return '';
    return data.map((v, i) => {
      const x = (i / 23) * chartWidth;
      const y = chartHeight - (v / maxValue) * chartHeight;
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
    }).join(' ');
  };

  // Generate area path for today's data
  const generateAreaPath = (data: number[], chartWidth: number, chartHeight: number) => {
    if (!data || data.length === 0) return '';
    const linePath = generatePath(data, chartWidth, chartHeight);
    return linePath + ` L ${chartWidth} ${chartHeight} L 0 ${chartHeight} Z`;
  };

  const chartWidth = 700;
  const chartHeight = 200;

  // Variation colors
  const varOntemColor = (bot.variation_vs_ontem || 0) >= 0 ? '#86efac' : '#fca5a5';
  const varSemanaColor = (bot.variation_vs_semana || 0) >= 0 ? '#86efac' : '#fca5a5';

  // Anomaly status
  const anomalyScore = bot.anomaly_score || 0;
  const isAnomaly = anomalyScore > 30;
  const isCritical = anomalyScore > 60;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        backdropFilter: 'blur(4px)'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#111827',
          borderRadius: '16px',
          padding: '24px',
          maxWidth: '900px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          border: '1px solid rgba(75, 85, 99, 0.5)',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '24px',
          paddingBottom: '16px',
          borderBottom: '1px solid rgba(75, 85, 99, 0.3)'
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <h2 style={{ color: '#fff', fontSize: '24px', fontWeight: '700', margin: 0 }}>
                {bot.bot_name}
              </h2>
              {isAnomaly && (
                <span style={{
                  backgroundColor: isCritical ? 'rgba(239, 68, 68, 0.2)' : 'rgba(251, 191, 36, 0.2)',
                  color: isCritical ? '#fca5a5' : '#fcd34d',
                  padding: '4px 10px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: '700'
                }}>
                  {isCritical ? 'CRITICO' : 'ANOMALIA'}
                </span>
              )}
            </div>
            <p style={{ color: '#6b7280', fontSize: '14px', margin: '4px 0 0 0' }}>
              {bot.client_name}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'rgba(75, 85, 99, 0.3)',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 16px',
              color: '#9ca3af',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(75, 85, 99, 0.5)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(75, 85, 99, 0.3)'}
          >
            Fechar (ESC)
          </button>
        </div>

        {/* Stats Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '16px',
          marginBottom: '24px'
        }}>
          {/* Today */}
          <div style={{
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            borderRadius: '12px',
            padding: '16px',
            border: '1px solid rgba(59, 130, 246, 0.2)'
          }}>
            <div style={{ color: '#6b7280', fontSize: '12px', marginBottom: '4px' }}>Hoje (ate {currentHour}h)</div>
            <div style={{ color: '#60a5fa', fontSize: '28px', fontWeight: '700' }}>
              {(bot.total_hoje || 0).toLocaleString()}
            </div>
          </div>

          {/* Yesterday */}
          <div style={{
            backgroundColor: 'rgba(167, 139, 250, 0.1)',
            borderRadius: '12px',
            padding: '16px',
            border: '1px solid rgba(167, 139, 250, 0.2)'
          }}>
            <div style={{ color: '#6b7280', fontSize: '12px', marginBottom: '4px' }}>Ontem (total)</div>
            <div style={{ color: '#a78bfa', fontSize: '28px', fontWeight: '700' }}>
              {(bot.total_ontem || 0).toLocaleString()}
            </div>
            <div style={{ color: varOntemColor, fontSize: '13px', fontWeight: '600', marginTop: '4px' }}>
              {(bot.variation_vs_ontem || 0) > 0 ? '+' : ''}{(bot.variation_vs_ontem || 0).toFixed(1)}% vs ontem
            </div>
          </div>

          {/* Last Week */}
          <div style={{
            backgroundColor: 'rgba(52, 211, 153, 0.1)',
            borderRadius: '12px',
            padding: '16px',
            border: '1px solid rgba(52, 211, 153, 0.2)'
          }}>
            <div style={{ color: '#6b7280', fontSize: '12px', marginBottom: '4px' }}>Semana passada (total)</div>
            <div style={{ color: '#34d399', fontSize: '28px', fontWeight: '700' }}>
              {(bot.total_semana || 0).toLocaleString()}
            </div>
            <div style={{ color: varSemanaColor, fontSize: '13px', fontWeight: '600', marginTop: '4px' }}>
              {(bot.variation_vs_semana || 0) > 0 ? '+' : ''}{(bot.variation_vs_semana || 0).toFixed(1)}% vs semana
            </div>
          </div>

          {/* Expected */}
          <div style={{
            backgroundColor: 'rgba(75, 85, 99, 0.2)',
            borderRadius: '12px',
            padding: '16px',
            border: '1px solid rgba(75, 85, 99, 0.3)'
          }}>
            <div style={{ color: '#6b7280', fontSize: '12px', marginBottom: '4px' }}>Esperado ate {currentHour}h</div>
            <div style={{ display: 'flex', gap: '16px' }}>
              <div>
                <div style={{ color: '#a78bfa', fontSize: '20px', fontWeight: '700' }}>
                  {(bot.expected_ontem || 0).toLocaleString()}
                </div>
                <div style={{ color: '#6b7280', fontSize: '10px' }}>base ontem</div>
              </div>
              <div>
                <div style={{ color: '#34d399', fontSize: '20px', fontWeight: '700' }}>
                  {(bot.expected_semana || 0).toLocaleString()}
                </div>
                <div style={{ color: '#6b7280', fontSize: '10px' }}>base semana</div>
              </div>
            </div>
            {((bot.deficit_ontem || 0) > 0 || (bot.deficit_semana || 0) > 0) && (
              <div style={{ color: '#fca5a5', fontSize: '12px', fontWeight: '600', marginTop: '8px' }}>
                Deficit: -{Math.max(bot.deficit_ontem || 0, bot.deficit_semana || 0).toLocaleString()}
              </div>
            )}
          </div>
        </div>

        {/* Hourly Chart */}
        <div style={{
          backgroundColor: 'rgba(17, 24, 39, 0.8)',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px',
          border: '1px solid rgba(75, 85, 99, 0.2)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ color: '#fff', fontSize: '16px', fontWeight: '600', margin: 0 }}>
              Comparativo Hora a Hora
            </h3>
            <div style={{ display: 'flex', gap: '16px', fontSize: '12px' }}>
              <span><span style={{ color: '#60a5fa' }}>━━</span> Hoje</span>
              <span><span style={{ color: '#a78bfa' }}>━━</span> Ontem</span>
              <span><span style={{ color: '#34d399' }}>┅┅</span> Semana</span>
            </div>
          </div>

          <svg width="100%" height={chartHeight + 40} viewBox={`0 0 ${chartWidth} ${chartHeight + 40}`} preserveAspectRatio="xMidYMid meet">
            {/* Grid lines */}
            {[0, 0.25, 0.5, 0.75, 1].map((pct) => (
              <g key={pct}>
                <line
                  x1={0}
                  y1={chartHeight * pct}
                  x2={chartWidth}
                  y2={chartHeight * pct}
                  stroke="rgba(75, 85, 99, 0.3)"
                  strokeDasharray="4 4"
                />
                <text
                  x={chartWidth + 5}
                  y={chartHeight * pct + 4}
                  fill="#6b7280"
                  fontSize="10"
                >
                  {Math.round(maxValue * (1 - pct))}
                </text>
              </g>
            ))}

            {/* Current hour indicator */}
            <line
              x1={(currentHour / 23) * chartWidth}
              y1={0}
              x2={(currentHour / 23) * chartWidth}
              y2={chartHeight}
              stroke="rgba(239, 68, 68, 0.5)"
              strokeWidth="2"
              strokeDasharray="4 4"
            />
            <text
              x={(currentHour / 23) * chartWidth}
              y={chartHeight + 25}
              fill="#ef4444"
              fontSize="10"
              textAnchor="middle"
            >
              {currentHour}h (agora)
            </text>

            {/* Today area */}
            <path
              d={generateAreaPath(hourly.hoje, chartWidth, chartHeight)}
              fill="rgba(59, 130, 246, 0.2)"
            />

            {/* Today line */}
            <path
              d={generatePath(hourly.hoje, chartWidth, chartHeight)}
              fill="none"
              stroke="#3B82F6"
              strokeWidth="3"
            />

            {/* Yesterday line */}
            <path
              d={generatePath(hourly.ontem, chartWidth, chartHeight)}
              fill="none"
              stroke="#A78BFA"
              strokeWidth="2"
            />

            {/* Last week line (dashed) */}
            <path
              d={generatePath(hourly.semana_anterior, chartWidth, chartHeight)}
              fill="none"
              stroke="#34D399"
              strokeWidth="2"
              strokeDasharray="6 3"
            />

            {/* Hour labels */}
            {[0, 6, 12, 18, 23].map((h) => (
              <text
                key={h}
                x={(h / 23) * chartWidth}
                y={chartHeight + 15}
                fill="#6b7280"
                fontSize="10"
                textAnchor="middle"
              >
                {h}h
              </text>
            ))}
          </svg>
        </div>

        {/* Hourly Breakdown Table */}
        <div style={{
          backgroundColor: 'rgba(17, 24, 39, 0.8)',
          borderRadius: '12px',
          padding: '20px',
          border: '1px solid rgba(75, 85, 99, 0.2)'
        }}>
          <h3 style={{ color: '#fff', fontSize: '16px', fontWeight: '600', margin: '0 0 16px 0' }}>
            Detalhamento por Hora
          </h3>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(75, 85, 99, 0.3)' }}>
                  <th style={{ padding: '8px', textAlign: 'left', color: '#9ca3af', fontWeight: '600' }}>Hora</th>
                  {Array.from({ length: 24 }, (_, i) => (
                    <th
                      key={i}
                      style={{
                        padding: '8px 4px',
                        textAlign: 'center',
                        color: i <= currentHour ? '#fff' : '#4b5563',
                        fontWeight: '500',
                        backgroundColor: i === currentHour ? 'rgba(239, 68, 68, 0.1)' : 'transparent'
                      }}
                    >
                      {i}
                    </th>
                  ))}
                  <th style={{ padding: '8px', textAlign: 'right', color: '#9ca3af', fontWeight: '600' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {/* Today */}
                <tr style={{ borderBottom: '1px solid rgba(75, 85, 99, 0.2)' }}>
                  <td style={{ padding: '8px', color: '#60a5fa', fontWeight: '600' }}>Hoje</td>
                  {hourly.hoje.map((val, i) => (
                    <td
                      key={i}
                      style={{
                        padding: '8px 4px',
                        textAlign: 'center',
                        color: i <= currentHour ? '#60a5fa' : '#4b5563',
                        backgroundColor: i === currentHour ? 'rgba(239, 68, 68, 0.1)' : 'transparent'
                      }}
                    >
                      {val || '-'}
                    </td>
                  ))}
                  <td style={{ padding: '8px', textAlign: 'right', color: '#60a5fa', fontWeight: '700' }}>
                    {(bot.total_hoje || 0).toLocaleString()}
                  </td>
                </tr>

                {/* Yesterday */}
                <tr style={{ borderBottom: '1px solid rgba(75, 85, 99, 0.2)' }}>
                  <td style={{ padding: '8px', color: '#a78bfa', fontWeight: '600' }}>Ontem</td>
                  {hourly.ontem.map((val, i) => (
                    <td
                      key={i}
                      style={{
                        padding: '8px 4px',
                        textAlign: 'center',
                        color: '#a78bfa',
                        backgroundColor: i === currentHour ? 'rgba(239, 68, 68, 0.1)' : 'transparent'
                      }}
                    >
                      {val || '-'}
                    </td>
                  ))}
                  <td style={{ padding: '8px', textAlign: 'right', color: '#a78bfa', fontWeight: '700' }}>
                    {(bot.total_ontem || 0).toLocaleString()}
                  </td>
                </tr>

                {/* Last Week */}
                <tr>
                  <td style={{ padding: '8px', color: '#34d399', fontWeight: '600' }}>Semana</td>
                  {hourly.semana_anterior.map((val, i) => (
                    <td
                      key={i}
                      style={{
                        padding: '8px 4px',
                        textAlign: 'center',
                        color: '#34d399',
                        backgroundColor: i === currentHour ? 'rgba(239, 68, 68, 0.1)' : 'transparent'
                      }}
                    >
                      {val || '-'}
                    </td>
                  ))}
                  <td style={{ padding: '8px', textAlign: 'right', color: '#34d399', fontWeight: '700' }}>
                    {(bot.total_semana || 0).toLocaleString()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BotDetailModal;
